import PageLayout from "@/components/feature/PageLayout";
import { useState } from "react";

const topics = [
  "Subscription Issues",
  "Billing & Payments",
  "Technical Support",
  "Account Access",
  "Refund Request",
  "General Inquiry",
  "Other"
];

export default function ContactSupport() {
  const [submitted, setSubmitted] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    topic: "",
    email: "",
    message: ""
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.topic) nextErrors.topic = "Please select a topic";
    if (!formData.email) {
      nextErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Please enter a valid email";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Message is required";
    } else if (formData.message.length < 20) {
      nextErrors.message = "Minimum 20 characters required";
    } else if (formData.message.length > 500) {
      nextErrors.message = "Message must be 500 characters or less";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    const data = new URLSearchParams();
    data.append("topic", formData.topic);
    data.append("email", formData.email);
    data.append("message", formData.message);

    let text = "";
    for (const [key, value] of Object.entries(formData)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: formData.email,
      text,
      problemType: formData.topic,
      to_email: "support@kingcraftech.com",
      subject: "Support Request"
    };

    try {
      const res = await fetch(
        "https://rocknlabs.com/api/support/request_form",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) throw new Error("Server error");

      setSubmitted(true);
    } catch {
      setErrorSubmit(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <style>{`
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px white inset;
      }
    `}</style>
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <div className="mb-1">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
            Support
          </span>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Contact Support
        </h2>
        <p className="text-gray-500 text-sm mb-8">
          Our support team is here to help. Reach out through any of the options
          below.
        </p>

        <div className="space-y-4">
          <div className="flex items-start gap-4 p-5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-xl flex-shrink-0">
              <i className="ri-mail-line text-gray-600 text-lg"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm mb-1">
                Email Support
              </p>
              <p className="text-gray-500 text-sm mb-2">
                Send us an email and we&apos;ll respond within 1–2 business
                days.
              </p>
              <a
                href="mailto:support@kingcraftech.com"
                className="text-gray-700 hover:text-gray-900 font-medium text-sm underline transition-colors"
              >
                support@kingcraftech.com
              </a>
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-xl flex-shrink-0">
              <i className="ri-phone-line text-gray-600 text-lg"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm mb-1">
                Phone Support
              </p>
              <p className="text-gray-500 text-sm mb-2">
                Call us directly during business hours (Mon–Fri, 9am–5pm GMT).
              </p>
              <a
                href="tel:+447451252956"
                className="text-gray-700 hover:text-gray-900 font-medium text-sm underline transition-colors"
              >
                +44 7451 252956
              </a>
            </div>
          </div>

          <div className="p-5 rounded-xl border border-gray-100 bg-gray-50">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-xl flex-shrink-0">
                <i className="ri-message-3-line text-gray-600 text-lg"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm mb-1">
                  Submit a Request
                </p>
                <p className="text-gray-500 text-sm">
                  Describe your issue and we&apos;ll get back to you as soon as
                  possible.
                </p>
              </div>
            </div>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-[#f5f0e8] rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-[#8b7355] text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Message Sent!
                </h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
                  We&apos;ve received your message and will get back to you
                  within 1–2 business days.
                </p>
                <button
                  onClick={() => {
                    setFormData({
                      email: "",
                      topic: "",
                      message: ""
                    });
                    setSubmitted(false);
                  }}
                  className="inline-flex items-center gap-2 bg-[#2d2f36] text-white px-8 py-3.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line text-base"></i>
                  Send Another Message
                </button>
              </div>
            ) : errorSubmit ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-[#f5f0e8] rounded-full mx-auto mb-4">
                  <i className="ri-close-line text-[#8b7355] text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Message Failed
                </h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto mb-6">
                  Something went wrong while sending your message. Please check
                  your connection and try again.
                </p>
                <button
                  onClick={() => {
                    setErrorSubmit(false);
                    setSubmitted(false);
                    setFormData({
                      email: "",
                      topic: "",
                      message: ""
                    });
                  }}
                  className="inline-flex items-center gap-2 bg-[#2d2f36] text-white px-8 py-3.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line text-base"></i>
                  Try Again
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="topic"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Topic
                  </label>
                  <div className="relative">
                    <select
                      id="topic"
                      name="topic"
                      value={formData.topic}
                      onChange={handleChange}
                      className={`appearance-none w-full px-3.5 py-2.5 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors cursor-pointer ${
                        errors.topic ? "border-red-300" : "border-gray-200"
                      }`}
                    >
                      <option value="">Select a topic</option>
                      {topics.map(t => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>

                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      <i className="ri-arrow-down-s-line text-gray-400 text-base"></i>
                    </div>
                    {errors.topic && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.topic}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors ${
                      errors.email ? "border-red-300" : "border-gray-200"
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe your issue in detail..."
                    rows={4}
                    maxLength={500}
                    className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors resize-none ${
                      errors.message ? "border-red-300" : "border-gray-200"
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {errors.message ? (
                      <p className="text-red-500 text-xs">{errors.message}</p>
                    ) : (
                      <span />
                    )}
                    <span className="text-xs text-gray-400">
                      {formData.message.length}/500
                    </span>
                  </div>
                </div>

                {errors.submit && (
                  <p className="text-red-500 text-sm">{errors.submit}</p>
                )}

                <button
                  type="submit"
                  className="w-full bg-[#2d2f36] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Submit Request
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 text-center text-sm text-gray-500 space-y-1">
          <p className="font-semibold text-gray-700">
            Kingcraft Technologies LTD
          </p>
          <p>
            International House, 100 Menzies Road, Hastings, TN38 9BB, United
            Kingdom
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
